import type { Locale } from "../../shared/dto";
import type { StaffMatrixClient } from "./model";

/**
 * The admin's "Parťák" filter on the client list (F173, Tomas Pohl
 * 2026-10-14). `null` = every client, `PARTNER_FILTER_NONE` = clients without
 * a partner, any other value = that partner's user id.
 */
export const PARTNER_FILTER_NONE = "__none__";

export type PartnerFilter = string | null;

export type PartnerFilterOption = { id: string; name: string | null; count: number };

/**
 * One option per partner that actually has a client in the list, by name.
 * Built from the rows themselves, so a partner without clients never offers an
 * empty choice and a renamed partner shows the name the rows carry.
 */
export function partnerFilterOptions(
  clients: ReadonlyArray<StaffMatrixClient>,
  locale: Locale
): PartnerFilterOption[] {
  const byId = new Map<string, PartnerFilterOption>();
  for (const client of clients) {
    if (!client.partner) continue;
    const option = byId.get(client.partner.id);
    if (option) option.count += 1;
    else byId.set(client.partner.id, { id: client.partner.id, name: client.partner.name, count: 1 });
  }
  return [...byId.values()].sort((a, b) =>
    (a.name ?? "").localeCompare(b.name ?? "", locale) || a.id.localeCompare(b.id));
}

/**
 * The rows the chosen partner owns. A partner id no longer present in the rows
 * (reassigned, deactivated) yields an empty list, which the list then explains
 * with its "filter matches nobody" state and a way back.
 */
export function filterClientsByPartner<T extends Pick<StaffMatrixClient, "partner">>(
  clients: ReadonlyArray<T>,
  partner: PartnerFilter
): T[] {
  if (partner === null) return [...clients];
  if (partner === PARTNER_FILTER_NONE) return clients.filter((client) => !client.partner);
  return clients.filter((client) => client.partner?.id === partner);
}
